import { useEffect, useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, TextField,
  FormControl, InputLabel, Select, MenuItem, Typography, Box, Divider, Stack,
} from "@mui/material";
import { asyncRequest } from "src/services/network";
import { alert } from "src/services/alert";

const TYPE_OPTIONS = [
  { value: 1, label: "金豆" },
  { value: 2, label: "银豆" },
];

const BOOL_OPTIONS = [
  { value: 1, label: "启用" },
  { value: 0, label: "禁用" },
];

const AMOUNT_MODE_OPTIONS = [
  { value: "FIXED", label: "固定金额" },
  { value: "RANDOM", label: "随机金额" },
];

const ISSUE_RULE_MODE_OPTIONS = [
  { value: "fixed", label: "按预约数量限制" },
  { value: "ratio", label: "按预约百分比限制" },
];

const DEFAULT_FORM = {
  grade_id: "",
  type: 1,
  session_start_hour: 10,
  session_start_minute: 0,
  session_duration_minutes: 30,
  session_offset_minutes: 0,
  default_display_reserve_num: 99,
  auto_issue_enabled: 1,
  issue_rule_mode: "fixed",
  issue_min_orders: 5,
  issue_max_orders: 50,
  issue_dispatch_ratio: 0.9,
  min_system_orders: 1,
  max_system_orders: 10,
  order_amount_mode: "FIXED",
  order_fixed_amount: 0,
  order_min_amount: 0,
  order_max_amount: 0,
  dispatch_enabled: 1,
  dispatch_before_minutes: 5,
  max_robot_orders_per_session: 0,
  robot_order_ratio: 0.6,
  robot_auto_reserve: 0,
  fee_rate: 0,
  pool_ratio: 0,
  superior_reward_ratio: 0,
  daily_buy_limit: 0,
  special_uid: "",
  order_recycle_enabled: 0,
  order_recycle_after_minutes: 0,
  status: 1,
};

/**
 * 场次配置编辑弹窗
 * @param {{ open, mode: 'create'|'edit', initial?, onClose, onSuccess }} props
 */
export default function SessionConfigEdit({ open, mode, initial, onClose, onSuccess }) {
  const [form, setForm] = useState(DEFAULT_FORM);
  const [saving, setSaving] = useState(false);
  const [grades, setGrades] = useState([]);

  useEffect(() => {
    if (open) {
      if (mode === "edit" && initial) {
        setForm({ ...DEFAULT_FORM, ...initial });
      } else {
        setForm(DEFAULT_FORM);
      }
      // 加载等级列表
      asyncRequest({ path: "/jingxi/grade/all", data: {} }).then(({ data }) => {
        setGrades(data || []);
      }).catch(() => {});
    }
  }, [open, mode, initial]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setNum = (k) => (e) => {
    const v = e.target.value;
    setForm({ ...form, [k]: v === "" ? "" : Number(v) });
  };

  async function handleSubmit() {
    // 基本校验
    if (form.grade_id === "" || form.grade_id === null) {
      return alert("请填写等级ID");
    }
    if (!form.type) {
      return alert("请选择类型");
    }
    const rateFields = ["fee_rate", "pool_ratio", "superior_reward_ratio", "robot_order_ratio"];
    for (const f of rateFields) {
      const v = Number(form[f]);
      if (isNaN(v) || v < 0 || v > 1) {
        return alert(`${f} 必须在 0~1 之间`);
      }
    }
    if (Number(form.min_system_orders) > Number(form.max_system_orders)) {
      return alert("min_system_orders 不能大于 max_system_orders");
    }
    if (form.issue_rule_mode === "fixed" && Number(form.issue_min_orders) > Number(form.issue_max_orders)) {
      return alert("本场最小单量不能大于最大单量");
    }
    if (form.issue_rule_mode === "ratio") {
      const r = Number(form.issue_dispatch_ratio);
      if (isNaN(r) || r <= 0 || r > 1) {
        return alert("本场派单比必须在 0~1 之间");
      }
    }

    setSaving(true);
    try {
      const path = mode === "edit" ? "/jingxi/session-config/update" : "/jingxi/session-config/create";
      const { msg } = await asyncRequest({ path, data: form });
      alert(msg || "保存成功", "success");
      onSuccess && onSuccess();
      onClose && onClose();
    } finally {
      setSaving(false);
    }
  }

  const field = (label, node, md = 4) => (
    <Grid item xs={12} sm={6} md={md}>
      <Stack spacing={0.5}>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        {node}
      </Stack>
    </Grid>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>{mode === "edit" ? "编辑场次配置" : "新建场次配置"}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ pt: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>基础信息</Typography>
          <Grid container spacing={2}>
            {field("等级 (grade_id)",
              <FormControl size="small">
                <Select value={form.grade_id} onChange={setNum("grade_id")} disabled={mode === "edit"}>
                  <MenuItem value="">请选择等级</MenuItem>
                  {grades.map(g => <MenuItem key={g.id} value={g.id}>{g.grade_name}</MenuItem>)}
                </Select>
              </FormControl>
            )}
            {field("类型 (type)",
              <FormControl size="small">
                <Select value={form.type} onChange={set("type")} disabled={mode === "edit"}>
                  {TYPE_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </Select>
              </FormControl>
            )}
            {field("状态 (status)",
              <FormControl size="small">
                <Select value={form.status} onChange={set("status")}>
                  {BOOL_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </Select>
              </FormControl>
            )}
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>场次时间</Typography>
          <Grid container spacing={2}>
            {field("开始小时 (0-23)",
              <TextField size="small" type="number" value={form.session_start_hour} onChange={setNum("session_start_hour")} />
            )}
            {field("开始分钟 (0-59)",
              <TextField size="small" type="number" value={form.session_start_minute} onChange={setNum("session_start_minute")} />
            )}
            {field("时长(分钟)",
              <TextField size="small" type="number" value={form.session_duration_minutes} onChange={setNum("session_duration_minutes")} />
            )}
            {field("偏移(分钟)",
              <TextField size="small" type="number" value={form.session_offset_minutes} onChange={setNum("session_offset_minutes")} />
            )}
            {field("默认预约美化数",
              <TextField size="small" type="number" value={form.default_display_reserve_num} onChange={setNum("default_display_reserve_num")} />
            )}
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>自动出单</Typography>
          <Grid container spacing={2}>
            {field("自动出单",
              <FormControl size="small">
                <Select value={form.auto_issue_enabled} onChange={set("auto_issue_enabled")}>
                  {BOOL_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </Select>
              </FormControl>
            )}
            {field("出单规则模式",
              <FormControl size="small">
                <Select value={form.issue_rule_mode} onChange={set("issue_rule_mode")}>
                  {ISSUE_RULE_MODE_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </Select>
              </FormControl>
            )}
            {form.issue_rule_mode === "fixed" ? (
              <>
                {field("本场最小单量",
                  <TextField size="small" type="number" value={form.issue_min_orders} onChange={setNum("issue_min_orders")} helperText="挂单不足时系统补单至此数量" />
                )}
                {field("本场最大单量",
                  <TextField size="small" type="number" value={form.issue_max_orders} onChange={setNum("issue_max_orders")} helperText="挂单超出时内置号收回超单" />
                )}
              </>
            ) : (
              field("本场派单比(0-1)",
                <TextField size="small" type="number" inputProps={{ step: 0.01 }} value={form.issue_dispatch_ratio} onChange={setNum("issue_dispatch_ratio")} helperText="如0.9=预约数的90%为目标单量" />
              )
            )}
            {field("金额模式",
              <FormControl size="small">
                <Select value={form.order_amount_mode} onChange={set("order_amount_mode")}>
                  {AMOUNT_MODE_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </Select>
              </FormControl>
            )}
            {form.order_amount_mode === "FIXED" ? (
              field("固定金额",
                <TextField size="small" type="number" value={form.order_fixed_amount} onChange={setNum("order_fixed_amount")} />
              )
            ) : (
              <>
                {field("最小金额",
                  <TextField size="small" type="number" value={form.order_min_amount || ""} onChange={setNum("order_min_amount")} />
                )}
                {field("最大金额",
                  <TextField size="small" type="number" value={form.order_max_amount || ""} onChange={setNum("order_max_amount")} />
                )}
              </>
            )}
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>派单 & 机器人</Typography>
          <Grid container spacing={2}>
            {field("派单开关",
              <FormControl size="small">
                <Select value={form.dispatch_enabled} onChange={set("dispatch_enabled")}>
                  {BOOL_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </Select>
              </FormControl>
            )}
            {field("派单提前分钟",
              <TextField size="small" type="number" value={form.dispatch_before_minutes} onChange={setNum("dispatch_before_minutes")} />
            )}
            {field("机器人单场最大",
              <TextField size="small" type="number" value={form.max_robot_orders_per_session} onChange={setNum("max_robot_orders_per_session")} />
            )}
            {field("机器人单占比(0-1)",
              <TextField size="small" type="number" inputProps={{ step: 0.01 }} value={form.robot_order_ratio} onChange={setNum("robot_order_ratio")} />
            )}
            {field("机器人自动预约",
              <FormControl size="small">
                <Select value={form.robot_auto_reserve} onChange={set("robot_auto_reserve")}>
                  {BOOL_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </Select>
              </FormControl>
            )}
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>费率 & 限额</Typography>
          <Grid container spacing={2}>
            {field("抢购银豆手续费率(0-1)",
              <TextField size="small" type="number" inputProps={{ step: 0.01 }} value={form.fee_rate} onChange={setNum("fee_rate")} />
            )}
            {field("收货入池金额(0-1)",
              <TextField size="small" type="number" inputProps={{ step: 0.01 }} value={form.pool_ratio} onChange={setNum("pool_ratio")} />
            )}
            {field("抢购上级银豆手续费奖励(0-1)",
              <TextField size="small" type="number" inputProps={{ step: 0.01 }} value={form.superior_reward_ratio} onChange={setNum("superior_reward_ratio")} />
            )}
            {field("每日购买上限",
              <TextField size="small" type="number" value={form.daily_buy_limit} onChange={setNum("daily_buy_limit")} />
            )}
            {field("特殊账号UID",
              <TextField size="small" value={form.special_uid} onChange={set("special_uid")} placeholder="逗号分隔多个UID" />, 8
            )}
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 1 }}>订单回收</Typography>
          <Grid container spacing={2}>
            {field("回收开关",
              <FormControl size="small">
                <Select value={form.order_recycle_enabled} onChange={set("order_recycle_enabled")}>
                  {BOOL_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                </Select>
              </FormControl>
            )}
            {field("回收延时分钟",
              <TextField size="small" type="number" value={form.order_recycle_after_minutes} onChange={setNum("order_recycle_after_minutes")} />
            )}
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>取消</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
          {saving ? "保存中..." : "保存"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
